/**
 * FleetManager - React Query Configuration
 *
 * Centralized configuration for React Query caching, query keys, and mutation helpers.
 *
 * @module lib/queryConfig
 *
 * Cache Strategy:
 * - Static data (organizations, sites): Long cache (5 min stale, 30 min gc)
 * - Reference data (vehicles, drivers): Medium cache (1 min stale, 10 min gc)
 * - Transactional data (claims, maintenance): Short cache (30s stale, 5 min gc)
 * - Real-time data (alerts, activity): No cache (always fresh)
 */

// =============================================================================
// QUERY KEYS FACTORY
// =============================================================================

/**
 * Query keys factory for consistent cache key management
 * Using factory pattern ensures type-safe, refactorable query keys
 *
 * @example
 * // List all vehicles
 * queryKey: queryKeys.vehicles.all
 *
 * // Single vehicle by ID
 * queryKey: queryKeys.vehicles.detail(123)
 *
 * // Filtered vehicles
 * queryKey: queryKeys.vehicles.list({ status: 'active' })
 */
export const queryKeys = {
  // Vehicles
  vehicles: {
    all: ['vehicles'],
    lists: () => [...queryKeys.vehicles.all, 'list'],
    list: (filters) => [...queryKeys.vehicles.lists(), filters],
    details: () => [...queryKeys.vehicles.all, 'detail'],
    detail: (id) => [...queryKeys.vehicles.details(), id],
    documents: (id) => [...queryKeys.vehicles.detail(id), 'documents'],
    insurance: (id) => [...queryKeys.vehicles.detail(id), 'insurance'],
    maintenance: (id) => [...queryKeys.vehicles.detail(id), 'maintenance'],
  },

  // Drivers
  drivers: {
    all: ['drivers'],
    lists: () => [...queryKeys.drivers.all, 'list'],
    list: (filters) => [...queryKeys.drivers.lists(), filters],
    details: () => [...queryKeys.drivers.all, 'detail'],
    detail: (id) => [...queryKeys.drivers.details(), id],
    licenses: (id) => [...queryKeys.drivers.detail(id), 'licenses'],
    riskProfile: (id) => [...queryKeys.drivers.detail(id), 'riskProfile'],
  },

  // Claims
  claims: {
    all: ['claims'],
    lists: () => [...queryKeys.claims.all, 'list'],
    list: (filters) => [...queryKeys.claims.lists(), filters],
    details: () => [...queryKeys.claims.all, 'detail'],
    detail: (id) => [...queryKeys.claims.details(), id],
    documents: (id) => [...queryKeys.claims.detail(id), 'documents'],
  },

  // Maintenance
  maintenance: {
    all: ['maintenance'],
    lists: () => [...queryKeys.maintenance.all, 'list'],
    list: (filters) => [...queryKeys.maintenance.lists(), filters],
    details: () => [...queryKeys.maintenance.all, 'detail'],
    detail: (id) => [...queryKeys.maintenance.details(), id],
  },

  // Insurance
  insurance: {
    all: ['insurance'],
    lists: () => [...queryKeys.insurance.all, 'list'],
    list: (filters) => [...queryKeys.insurance.lists(), filters],
    details: () => [...queryKeys.insurance.all, 'detail'],
    detail: (id) => [...queryKeys.insurance.details(), id],
    catalog: () => [...queryKeys.insurance.all, 'catalog'],
  },

  // Fuel Logs
  fuelLogs: {
    all: ['fuelLogs'],
    lists: () => [...queryKeys.fuelLogs.all, 'list'],
    list: (filters) => [...queryKeys.fuelLogs.lists(), filters],
    detail: (id) => [...queryKeys.fuelLogs.all, 'detail', id],
  },

  // Inspections
  inspections: {
    all: ['inspections'],
    lists: () => [...queryKeys.inspections.all, 'list'],
    list: (filters) => [...queryKeys.inspections.lists(), filters],
    detail: (id) => [...queryKeys.inspections.all, 'detail', id],
  },

  // Organizations (static data)
  organizations: {
    all: ['organizations'],
    detail: (id) => [...queryKeys.organizations.all, 'detail', id],
  },

  // Working Sites (static data)
  workingSites: {
    all: ['workingSites'],
    detail: (id) => [...queryKeys.workingSites.all, 'detail', id],
  },

  // Events
  events: {
    all: ['events'],
    lists: () => [...queryKeys.events.all, 'list'],
    list: (filters) => [...queryKeys.events.lists(), filters],
    detail: (id) => [...queryKeys.events.all, 'detail', id],
  },

  // Expenses
  expenses: {
    all: ['expenses'],
    lists: () => [...queryKeys.expenses.all, 'list'],
    list: (filters) => [...queryKeys.expenses.lists(), filters],
    detail: (id) => [...queryKeys.expenses.all, 'detail', id],
  },

  // Trips
  trips: {
    all: ['trips'],
    lists: () => [...queryKeys.trips.all, 'list'],
    list: (filters) => [...queryKeys.trips.lists(), filters],
    detail: (id) => [...queryKeys.trips.all, 'detail', id],
  },

  // Assignments
  assignments: {
    all: ['assignments'],
    byVehicle: (vehicleId) => [...queryKeys.assignments.all, 'vehicle', vehicleId],
    byDriver: (driverId) => [...queryKeys.assignments.all, 'driver', driverId],
  },

  // Documents
  documents: {
    all: ['documents'],
    byEntity: (entityType, entityId) => [...queryKeys.documents.all, entityType, entityId],
  },

  // Tools
  tools: {
    all: ['tools'],
    detail: (id) => [...queryKeys.tools.all, 'detail', id],
    sets: () => [...queryKeys.tools.all, 'sets'],
    setDetail: (id) => [...queryKeys.tools.sets(), id],
  },

  // Fines
  fines: {
    all: ['fines'],
    lists: () => [...queryKeys.fines.all, 'list'],
    list: (filters) => [...queryKeys.fines.lists(), filters],
    detail: (id) => [...queryKeys.fines.all, 'detail', id],
    stats: () => [...queryKeys.fines.all, 'stats'],
  },

  // Suppliers
  suppliers: {
    all: ['suppliers'],
    byCategory: (category) => [...queryKeys.suppliers.all, 'category', category],
    detail: (id) => [...queryKeys.suppliers.all, 'detail', id],
  },

  // User/Auth
  user: {
    current: ['user', 'current'],
    profile: ['user', 'profile'],
  },

  // Admin/Monitor
  monitor: {
    health: ['monitor', 'health'],
    activity: ['monitor', 'activity'],
    features: ['monitor', 'features'],
  },
};

// =============================================================================
// CACHE TIME CONFIGURATIONS
// =============================================================================

/**
 * Stale times by data category (in milliseconds)
 * staleTime: How long data is considered fresh (no refetch)
 */
export const staleTimes = {
  /** Static data that rarely changes (orgs, sites, catalog) */
  static: 5 * 60 * 1000, // 5 minutes

  /** Reference data that changes occasionally (vehicles, drivers) */
  reference: 60 * 1000, // 1 minute

  /** Transactional data that changes frequently (claims, maintenance) */
  transactional: 30 * 1000, // 30 seconds

  /** Real-time data that should always be fresh */
  realtime: 0, // Always stale, always refetch

  /** User session data */
  session: 5 * 60 * 1000, // 5 minutes
};

/**
 * Garbage collection times by data category (in milliseconds)
 * gcTime: How long inactive data stays in cache (v5 renamed from cacheTime)
 */
export const gcTimes = {
  /** Static data - keep longer */
  static: 30 * 60 * 1000, // 30 minutes

  /** Reference data */
  reference: 10 * 60 * 1000, // 10 minutes

  /** Transactional data */
  transactional: 5 * 60 * 1000, // 5 minutes

  /** Real-time data - short cache */
  realtime: 60 * 1000, // 1 minute

  /** User session */
  session: 30 * 60 * 1000, // 30 minutes
};

// =============================================================================
// QUERY OPTIONS PRESETS
// =============================================================================

/**
 * Pre-configured query options for common use cases
 *
 * @example
 * useQuery({
 *   queryKey: queryKeys.organizations.all,
 *   queryFn: fetchOrganizations,
 *   ...queryOptions.static
 * })
 */
export const queryOptions = {
  /** For static/rarely-changing data */
  static: {
    staleTime: staleTimes.static,
    gcTime: gcTimes.static,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  },

  /** For reference data (vehicles, drivers) */
  reference: {
    staleTime: staleTimes.reference,
    gcTime: gcTimes.reference,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  },

  /** For transactional data (claims, maintenance) */
  transactional: {
    staleTime: staleTimes.transactional,
    gcTime: gcTimes.transactional,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  },

  /** For real-time data (activity feed, alerts) */
  realtime: {
    staleTime: staleTimes.realtime,
    gcTime: gcTimes.realtime,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchInterval: 30000, // Poll every 30 seconds
  },

  /** For user session data */
  session: {
    staleTime: staleTimes.session,
    gcTime: gcTimes.session,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  },
};

// =============================================================================
// QUERY CLIENT CONFIGURATION
// =============================================================================

/**
 * Default options for QueryClient
 * Used in index.js when creating the QueryClient
 */
export const defaultQueryClientOptions = {
  queries: {
    // Default stale time (reference data)
    staleTime: staleTimes.reference,

    // Default gc time
    gcTime: gcTimes.reference,

    // Don't refetch on window focus by default (user controls this)
    refetchOnWindowFocus: false,

    // Retry disabled to prevent infinite loops from 404 errors
    // TODO: Re-enable with proper error handling after fixing auth/tenant endpoint
    retry: false,

    // Retry delay (not used when retry=false)
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),

    // Network mode: always fetch, even offline (will use cache)
    networkMode: 'offlineFirst',

    // DEBUG: Log all query lifecycle events
    onError: (error, query) => {
      console.log('[React Query ERROR]', query.queryKey, error);
    },
    onSuccess: (data, query) => {
      console.log('[React Query SUCCESS]', query.queryKey);
    },
  },
  mutations: {
    // Retry mutations once
    retry: 1,

    // Network mode for mutations
    networkMode: 'offlineFirst',
  },
};

// =============================================================================
// INVALIDATION HELPERS
// =============================================================================

/**
 * Invalidation patterns for common operations
 * Use with queryClient.invalidateQueries()
 *
 * @example
 * // After creating a vehicle
 * queryClient.invalidateQueries(invalidationPatterns.vehicleCreated)
 *
 * // After updating a specific vehicle
 * queryClient.invalidateQueries({
 *   queryKey: queryKeys.vehicles.detail(vehicleId)
 * })
 */
export const invalidationPatterns = {
  /** After creating a vehicle */
  vehicleCreated: { queryKey: queryKeys.vehicles.all },

  /** After updating/deleting a vehicle */
  vehicleModified: (id) => ({
    queryKey: queryKeys.vehicles.detail(id),
    refetchType: 'active',
  }),

  /** After driver changes */
  driverModified: (id) => ({
    queryKey: queryKeys.drivers.detail(id),
    refetchType: 'active',
  }),

  /** After claim status change */
  claimModified: (id) => ({
    queryKey: queryKeys.claims.detail(id),
    refetchType: 'active',
  }),

  /** After maintenance status change */
  maintenanceModified: (id) => ({
    queryKey: queryKeys.maintenance.detail(id),
    refetchType: 'active',
  }),

  /** After any CRUD operation - invalidate list */
  listInvalidation: (entityKey) => ({
    queryKey: [entityKey],
    exact: false,
  }),
};

// =============================================================================
// EXPORTS
// =============================================================================

const queryConfig = {
  queryKeys,
  staleTimes,
  gcTimes,
  queryOptions,
  defaultQueryClientOptions,
  invalidationPatterns,
};

export default queryConfig;
